import { computed } from 'vue';
import {
  format as dateFnsFormat,
  formatDistanceToNow,
  fromUnixTime,
} from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { useI18n } from 'vue-i18n';

const DATE_FNS_LOCALES = { pt_BR: ptBR };
const BCP47_TAGS = { pt_BR: 'pt-BR' };

const toDate = value =>
  typeof value === 'number' ? fromUnixTime(value) : new Date(value);

/**
 * Locale-aware date/number helpers that resolve the locale from the current
 * i18n language instead of hardcoding pt-BR.
 * - dateFnsLocale: date-fns locale object (for `format`/`formatDistanceToNow`)
 * - localeTag: BCP-47 tag (for `toLocaleString`/`Intl.*`)
 */
export function useDateLocale() {
  const { locale } = useI18n();

  const dateFnsLocale = computed(() => DATE_FNS_LOCALES[locale.value] || enUS);
  const localeTag = computed(() => BCP47_TAGS[locale.value] || 'en');

  const timeAgo = time => {
    if (!time) return '';
    return formatDistanceToNow(toDate(time), {
      addSuffix: true,
      locale: dateFnsLocale.value,
    });
  };

  const formatDate = (value, fmt) => {
    if (!value) return '';
    return dateFnsFormat(toDate(value), fmt, { locale: dateFnsLocale.value });
  };

  return { dateFnsLocale, localeTag, timeAgo, formatDate };
}

/**
 * Locale-aware relative time ("há 5 horas" / "5 hours ago").
 */
export function useTimeAgo() {
  const { timeAgo } = useDateLocale();
  return { timeAgo };
}
